// src/components/LibraryScreen.jsx
import { useState } from "react";
import {
  addTaskTemplate, updateTaskTemplate, deleteTaskTemplate,
  addEventBundle, updateEventBundle, deleteEventBundle,
  fireEvent, pullFromLibrary,
} from "../lib/db";

const iS = {
  width: "100%", background: "#0F172A", border: "1px solid #2D3748",
  color: "#F1F5F9", padding: "8px 10px", borderRadius: 8, fontSize: 13,
  outline: "none", fontFamily: "inherit", boxSizing: "border-box",
};
const lS = {
  display: "block", fontSize: 10, fontWeight: 700, color: "#64748B",
  textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4,
};

const BLANK_TPL    = { title: "", category: "", type: "standalone", notes: "", defaultAssignedTo: "" };
const BLANK_BUNDLE = { name: "", description: "", anchorLabel: "Start date", tasks: [] };
const BLANK_TASK   = { title: "", category: "", dayOffset: 0, defaultAssignedTo: "manager", notes: "" };

// ── TaskDraftForm (inline step editor inside bundle form) ─────────────────
function TaskDraftForm({ draft, setDraft, roleCategories, members, user, accent, onSave, onCancel }) {
  return (
    <div style={{ background: "#0F172A", border: "1px solid #374151", borderRadius: 8, padding: 12, marginBottom: 4 }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <div style={{ width: 68 }}>
          <label style={lS}>Day</label>
          <input type="number" value={draft.dayOffset}
            onChange={e => setDraft(d => ({ ...d, dayOffset: parseInt(e.target.value) || 0 }))}
            style={{ ...iS, padding: "6px 8px" }} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={lS}>Title</label>
          <input value={draft.title}
            onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
            placeholder="Task step title" style={{ ...iS, padding: "6px 8px" }} autoFocus />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <label style={lS}>Category</label>
          <select value={draft.category}
            onChange={e => setDraft(d => ({ ...d, category: e.target.value }))}
            style={{ ...iS, padding: "6px 8px" }}>
            {roleCategories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label style={lS}>Assign to</label>
          <select value={draft.defaultAssignedTo}
            onChange={e => setDraft(d => ({ ...d, defaultAssignedTo: e.target.value }))}
            style={{ ...iS, padding: "6px 8px" }}>
            <option value="">Unassigned</option>
            <option value="owner">Owner</option>
            <option value="manager">Manager</option>
            {members.map(m => (
              <option key={m.uid} value={m.uid}>
                {m.uid === user.uid ? "Me" : m.displayName || m.email}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div style={{ marginBottom: 8 }}>
        <label style={lS}>Notes (optional)</label>
        <input value={draft.notes}
          onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))}
          placeholder="e.g. Provider completes this independently"
          style={{ ...iS, padding: "6px 8px" }} />
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={onCancel}
          style={{ flex: 1, background: "#1E293B", color: "#94A3B8", border: "none", padding: "7px 8px", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>
          Cancel
        </button>
        <button onClick={onSave}
          style={{ flex: 2, background: accent, color: "#0F172A", border: "none", padding: "7px 8px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          Save step
        </button>
      </div>
    </div>
  );
}

// ── LibraryScreen ─────────────────────────────────────────────────────────
export function LibraryScreen({ activeRole, roleCategories, members, member, user, templates, bundles, onClose, showToast }) {
  const canEdit = member?.permission === "owner" || member?.permission === "manager";

  const [section, setSection]           = useState("templates");
  const [search, setSearch]             = useState("");

  // Template CRUD state
  const [showTplForm, setShowTplForm]   = useState(false);
  const [editingTpl, setEditingTpl]     = useState(null);
  const [tplForm, setTplForm]           = useState(BLANK_TPL);

  // Pull-from-library modal
  const [pullTarget, setPullTarget]     = useState(null);
  const [pullDue, setPullDue]           = useState("");
  const [pullAssignee, setPullAssignee] = useState("");
  const [pulling, setPulling]           = useState(false);

  // Bundle CRUD state
  const [showBundleForm, setShowBundleForm] = useState(false);
  const [editingBundle, setEditingBundle]   = useState(null);
  const [bundleForm, setBundleForm]         = useState(BLANK_BUNDLE);
  const [editingTaskIdx, setEditingTaskIdx] = useState(null);
  const [taskDraft, setTaskDraft]           = useState(BLANK_TASK);

  // Fire-event modal
  const [fireTarget, setFireTarget]           = useState(null);
  const [fireContextName, setFireContextName] = useState("");
  const [fireAnchorDate, setFireAnchorDate]   = useState("");
  const [firing, setFiring]                   = useState(false);

  const accent = activeRole.accent;

  // Helpers
  const resolveToUid = (role) => {
    if (!role)             return "";
    if (role === "owner")   return members.find(m => m.permission === "owner")?.uid   || "";
    if (role === "manager") return members.find(m => m.permission === "manager")?.uid || "";
    return role;
  };
  const resolveAssigneeName = (val) => {
    if (!val)               return "Unassigned";
    if (val === "owner")    return "Owner";
    if (val === "manager")  return "Manager";
    const m = members.find(m => m.uid === val);
    return m ? (m.uid === user.uid ? "You" : m.displayName || m.email) : "Unknown";
  };
  const typeBadge = (type) => ({
    standalone: { label: "Standalone", color: "#2DD4BF" },
    event:      { label: "Event only", color: "#F59E0B" },
    both:       { label: "Both",       color: "#8B5CF6" },
  }[type] || { label: type || "Standalone", color: "#2DD4BF" });

  const roleTemplates = templates.filter(t =>
    t.roleId === activeRole.id &&
    (!search || t.title.toLowerCase().includes(search.toLowerCase()))
  );
  const roleBundles = bundles.filter(b => b.roleId === activeRole.id);

  // ── Template handlers ───────────────────────────────────────────────────
  const openAddTpl = () => {
    setTplForm({ ...BLANK_TPL, category: roleCategories[0]?.id || "" });
    setEditingTpl(null);
    setShowTplForm(true);
  };
  const openEditTpl = (t) => {
    setTplForm({ title: t.title, category: t.category, type: t.type || "standalone", notes: t.notes || "", defaultAssignedTo: t.defaultAssignedTo || "" });
    setEditingTpl(t);
    setShowTplForm(true);
  };
  const saveTpl = async () => {
    if (!tplForm.title.trim()) return;
    try {
      if (editingTpl) {
        await updateTaskTemplate(editingTpl.id, tplForm);
        showToast("Template updated");
      } else {
        await addTaskTemplate({ ...tplForm, roleId: activeRole.id }, user.uid);
        showToast("Template added");
      }
      setShowTplForm(false);
    } catch { showToast("Failed to save template", "error"); }
  };
  const deleteTpl = async (t) => {
    if (!window.confirm(`Delete template "${t.title}"?`)) return;
    try {
      await deleteTaskTemplate(t.id);
      showToast("Template deleted", "info");
    } catch { showToast("Failed to delete", "error"); }
  };

  const openPull = (t) => {
    setPullTarget(t);
    setPullDue("");
    setPullAssignee(resolveToUid(t.defaultAssignedTo));
  };
  const confirmPull = async () => {
    if (!pullDue) { showToast("Please set a due date", "error"); return; }
    setPulling(true);
    try {
      await pullFromLibrary(pullTarget, pullDue, pullAssignee, user.uid);
      showToast("Task added to board");
      setPullTarget(null);
    } catch { showToast("Failed to add task", "error"); }
    setPulling(false);
  };

  // ── Bundle handlers ─────────────────────────────────────────────────────
  const openAddBundle = () => {
    setBundleForm({ ...BLANK_BUNDLE });
    setEditingBundle(null);
    setEditingTaskIdx(null);
    setShowBundleForm(true);
  };
  const openEditBundle = (b) => {
    setBundleForm({ name: b.name, description: b.description || "", anchorLabel: b.anchorLabel, tasks: [...(b.tasks || [])] });
    setEditingBundle(b);
    setEditingTaskIdx(null);
    setShowBundleForm(true);
  };
  const saveBundle = async () => {
    if (!bundleForm.name.trim()) return;
    try {
      if (editingBundle) {
        await updateEventBundle(editingBundle.id, bundleForm);
        showToast("Bundle updated");
      } else {
        await addEventBundle({ ...bundleForm, roleId: activeRole.id }, user.uid);
        showToast("Bundle created");
      }
      setShowBundleForm(false);
    } catch { showToast("Failed to save bundle", "error"); }
  };
  const deleteBundle = async (b) => {
    if (!window.confirm(`Delete bundle "${b.name}"?`)) return;
    try {
      await deleteEventBundle(b.id);
      showToast("Bundle deleted", "info");
    } catch { showToast("Failed to delete", "error"); }
  };

  // Bundle task list editing
  const startAddTask = () => {
    setTaskDraft({ ...BLANK_TASK, category: roleCategories[0]?.id || "" });
    setEditingTaskIdx("new");
  };
  const startEditTask = (idx) => {
    setTaskDraft({ ...bundleForm.tasks[idx] });
    setEditingTaskIdx(idx);
  };
  const saveTask = () => {
    if (!taskDraft.title.trim()) return;
    if (editingTaskIdx === "new") {
      setBundleForm(f => ({ ...f, tasks: [...f.tasks, taskDraft] }));
    } else {
      setBundleForm(f => {
        const tasks = [...f.tasks];
        tasks[editingTaskIdx] = taskDraft;
        return { ...f, tasks };
      });
    }
    setEditingTaskIdx(null);
  };
  const removeTask = (idx) => {
    setBundleForm(f => ({ ...f, tasks: f.tasks.filter((_, i) => i !== idx) }));
    if (editingTaskIdx === idx) setEditingTaskIdx(null);
  };

  // ── Fire event handlers ─────────────────────────────────────────────────
  const openFire = (b) => {
    setFireTarget(b);
    setFireContextName("");
    setFireAnchorDate("");
  };
  const getFirePreview = () => {
    if (!fireAnchorDate || !fireTarget) return [];
    const anchor = new Date(fireAnchorDate + "T00:00:00");
    return (fireTarget.tasks || []).map(task => {
      const due = new Date(anchor);
      due.setDate(due.getDate() + task.dayOffset);
      const assignee = task.defaultAssignedTo === "owner"
        ? members.find(m => m.permission === "owner")?.displayName || "Owner"
        : task.defaultAssignedTo === "manager"
        ? members.find(m => m.permission === "manager")?.displayName || "Manager"
        : task.defaultAssignedTo
        ? members.find(m => m.uid === task.defaultAssignedTo)?.displayName || "Unknown"
        : "Unassigned";
      return {
        title: fireContextName ? `${task.title} — ${fireContextName}` : task.title,
        due: due.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        assignee,
      };
    });
  };
  const confirmFire = async () => {
    if (!fireAnchorDate) { showToast("Please set anchor date", "error"); return; }
    setFiring(true);
    try {
      await fireEvent(fireTarget, fireAnchorDate, fireContextName, user.uid, members);
      showToast(`${fireTarget.tasks?.length || 0} tasks added to board`);
      setFireTarget(null);
    } catch { showToast("Failed to generate tasks", "error"); }
    setFiring(false);
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 300, overflowY: "auto", paddingTop: "env(safe-area-inset-top)" }} className="lib-scroll">
      <style>{`
        /* Main library overlay scrollbar — wide, accent-coloured */
        .lib-scroll::-webkit-scrollbar { width: 8px; }
        .lib-scroll::-webkit-scrollbar-track { background: #0F172A; }
        .lib-scroll::-webkit-scrollbar-thumb { background: ${accent}; border-radius: 4px; }
        .lib-scroll::-webkit-scrollbar-thumb:hover { background: ${accent}cc; }
        .lib-scroll { scrollbar-width: auto; scrollbar-color: ${accent} #0F172A; }

        /* Fire-modal sheet scrollbar — medium, slate */
        .lib-modal-scroll::-webkit-scrollbar { width: 6px; }
        .lib-modal-scroll::-webkit-scrollbar-track { background: #1E293B; }
        .lib-modal-scroll::-webkit-scrollbar-thumb { background: #475569; border-radius: 3px; }
        .lib-modal-scroll::-webkit-scrollbar-thumb:hover { background: #64748B; }
        .lib-modal-scroll { scrollbar-width: thin; scrollbar-color: #475569 #1E293B; }

        /* Inner preview list scrollbar — narrow, amber, clearly nested */
        .lib-preview-scroll::-webkit-scrollbar { width: 4px; }
        .lib-preview-scroll::-webkit-scrollbar-track { background: #0F172A; border-radius: 2px; }
        .lib-preview-scroll::-webkit-scrollbar-thumb { background: #F59E0B; border-radius: 2px; }
        .lib-preview-scroll { scrollbar-width: thin; scrollbar-color: #F59E0B #0F172A; }
      `}</style>
      <div style={{ maxWidth: 560, margin: "0 auto", padding: "20px 16px 120px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#F1F5F9" }}>Task Library</div>
            <div style={{ fontSize: 12, color: "#64748B" }}>{activeRole.shortName}</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#64748B", cursor: "pointer", fontSize: 20, lineHeight: 1 }}>✕</button>
        </div>

        {/* Section toggle */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20, background: "#1E293B", borderRadius: 10, padding: 4 }}>
          {["templates", "bundles"].map(s => (
            <button key={s} onClick={() => setSection(s)}
              style={{ flex: 1, background: section === s ? accent : "transparent", color: section === s ? "#0F172A" : "#64748B", border: "none", padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              {s === "templates" ? "Task Templates" : "Event Bundles"}
            </button>
          ))}
        </div>

        {/* ── TEMPLATES ────────────────────────────────────────────────── */}
        {section === "templates" && (
          <div>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search templates…" style={{ ...iS, marginBottom: 14 }} />

            {roleTemplates.length === 0 && !showTplForm && (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ fontSize: 13, color: "#475569" }}>No templates yet for {activeRole.shortName}</div>
                {canEdit && <div style={{ fontSize: 11, color: "#374151", marginTop: 4 }}>Add reusable tasks below</div>}
              </div>
            )}

            {roleTemplates.map(t => {
              const cat = roleCategories.find(c => c.id === t.category);
              const badge = typeBadge(t.type);
              return (
                <div key={t.id} style={{ background: "#1E293B", border: "1px solid #2D3748", borderRadius: 10, padding: "12px 14px", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#F1F5F9", marginBottom: 5 }}>{t.title}</div>
                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
                        {cat && <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 8, background: cat.color + "22", color: cat.color }}>{cat.label}</span>}
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 8, background: badge.color + "22", color: badge.color }}>{badge.label}</span>
                        {t.defaultAssignedTo && <span style={{ fontSize: 10, color: "#475569" }}>→ {resolveAssigneeName(t.defaultAssignedTo)}</span>}
                      </div>
                      {t.notes && <div style={{ fontSize: 11, color: "#475569", marginTop: 5, lineHeight: 1.4 }}>{t.notes}</div>}
                    </div>
                    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                      {(t.type !== "event") && (
                        <button onClick={() => openPull(t)}
                          style={{ background: accent, color: "#0F172A", border: "none", padding: "5px 10px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                          + Add
                        </button>
                      )}
                      {canEdit && <>
                        <button onClick={() => openEditTpl(t)} style={{ background: "transparent", border: "none", color: "#475569", cursor: "pointer", fontSize: 14, padding: "3px 4px" }}>✏️</button>
                        <button onClick={() => deleteTpl(t)} style={{ background: "transparent", border: "none", color: "#475569", cursor: "pointer", fontSize: 14, padding: "3px 4px" }}>🗑</button>
                      </>}
                    </div>
                  </div>
                </div>
              );
            })}

            {canEdit && !showTplForm && (
              <button onClick={openAddTpl}
                style={{ width: "100%", background: "#1E293B", border: "1px dashed #2D3748", color: "#64748B", padding: 14, borderRadius: 10, fontSize: 13, cursor: "pointer", fontWeight: 600, marginTop: 4 }}>
                + Add template
              </button>
            )}

            {showTplForm && (
              <div style={{ background: "#1E293B", border: "1px solid #2D3748", borderRadius: 12, padding: 20, marginTop: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#F1F5F9", marginBottom: 16 }}>
                  {editingTpl ? "Edit template" : "New template"}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div>
                    <label style={lS}>Title</label>
                    <input value={tplForm.title} onChange={e => setTplForm(f => ({ ...f, title: e.target.value }))}
                      placeholder="Task title" style={iS} autoFocus />
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <label style={lS}>Category</label>
                      <select value={tplForm.category} onChange={e => setTplForm(f => ({ ...f, category: e.target.value }))} style={iS}>
                        {roleCategories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={lS}>Type</label>
                      <select value={tplForm.type} onChange={e => setTplForm(f => ({ ...f, type: e.target.value }))} style={iS}>
                        <option value="standalone">Standalone</option>
                        <option value="event">Event only</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label style={lS}>Default assignee</label>
                    <select value={tplForm.defaultAssignedTo} onChange={e => setTplForm(f => ({ ...f, defaultAssignedTo: e.target.value }))} style={iS}>
                      <option value="">Unassigned</option>
                      <option value="owner">Owner</option>
                      <option value="manager">Manager</option>
                      {members.map(m => (
                        <option key={m.uid} value={m.uid}>
                          {m.uid === user.uid ? "Me" : m.displayName || m.email}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={lS}>Notes</label>
                    <textarea value={tplForm.notes} onChange={e => setTplForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder="Default notes for this task…"
                      style={{ ...iS, height: 60, resize: "vertical" }} />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                  <button onClick={() => setShowTplForm(false)}
                    style={{ flex: 1, background: "#2D3748", color: "#94A3B8", border: "none", padding: 10, borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Cancel</button>
                  <button onClick={saveTpl}
                    style={{ flex: 2, background: accent, color: "#0F172A", border: "none", padding: 10, borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
                    {editingTpl ? "Save changes" : "Add template"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── BUNDLES ──────────────────────────────────────────────────── */}
        {section === "bundles" && (
          <div>
            {roleBundles.length === 0 && !showBundleForm && (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ fontSize: 13, color: "#475569" }}>No event bundles for {activeRole.shortName}</div>
              </div>
            )}

            {roleBundles.map(b => (
              <div key={b.id} style={{ background: "#1E293B", border: "1px solid #2D3748", borderRadius: 10, padding: "12px 14px", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#F1F5F9", marginBottom: 3 }}>{b.name}</div>
                    <div style={{ fontSize: 11, color: "#64748B" }}>
                      {b.tasks?.length || 0} tasks · Anchor: {b.anchorLabel}
                    </div>
                    {b.description && <div style={{ fontSize: 11, color: "#475569", marginTop: 4, lineHeight: 1.4 }}>{b.description}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                    <button onClick={() => openFire(b)}
                      style={{ background: accent, color: "#0F172A", border: "none", padding: "5px 10px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                      Fire
                    </button>
                    {canEdit && <>
                      <button onClick={() => openEditBundle(b)} style={{ background: "transparent", border: "none", color: "#475569", cursor: "pointer", fontSize: 14, padding: "3px 4px" }}>✏️</button>
                      <button onClick={() => deleteBundle(b)} style={{ background: "transparent", border: "none", color: "#475569", cursor: "pointer", fontSize: 14, padding: "3px 4px" }}>🗑</button>
                    </>}
                  </div>
                </div>
              </div>
            ))}

            {canEdit && !showBundleForm && (
              <button onClick={openAddBundle}
                style={{ width: "100%", background: "#1E293B", border: "1px dashed #2D3748", color: "#64748B", padding: 14, borderRadius: 10, fontSize: 13, cursor: "pointer", fontWeight: 600, marginTop: 4 }}>
                + Add event bundle
              </button>
            )}

            {showBundleForm && (
              <div style={{ background: "#1E293B", border: "1px solid #2D3748", borderRadius: 12, padding: 20, marginTop: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#F1F5F9", marginBottom: 16 }}>
                  {editingBundle ? "Edit bundle" : "New event bundle"}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div>
                    <label style={lS}>Bundle name</label>
                    <input value={bundleForm.name} onChange={e => setBundleForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. New Provider Hire" style={iS} autoFocus />
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <div style={{ flex: 2 }}>
                      <label style={lS}>Description</label>
                      <input value={bundleForm.description} onChange={e => setBundleForm(f => ({ ...f, description: e.target.value }))}
                        placeholder="Optional description" style={iS} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={lS}>Anchor label</label>
                      <input value={bundleForm.anchorLabel} onChange={e => setBundleForm(f => ({ ...f, anchorLabel: e.target.value }))}
                        placeholder="e.g. Start date" style={iS} />
                    </div>
                  </div>

                  {/* Task step list */}
                  <div>
                    <label style={{ ...lS, marginBottom: 8 }}>Task steps ({bundleForm.tasks.length})</label>
                    {bundleForm.tasks.map((task, idx) => (
                      <div key={idx}>
                        {editingTaskIdx === idx ? (
                          <TaskDraftForm draft={taskDraft} setDraft={setTaskDraft}
                            roleCategories={roleCategories} members={members} user={user}
                            accent={accent} onSave={saveTask} onCancel={() => setEditingTaskIdx(null)} />
                        ) : (
                          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", background: "#0F172A", borderRadius: 8, marginBottom: 4 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: "#475569", minWidth: 30, textAlign: "right" }}>
                              {task.dayOffset >= 0 ? "+" : ""}{task.dayOffset}d
                            </span>
                            <span style={{ flex: 1, fontSize: 12, color: "#F1F5F9", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{task.title}</span>
                            <span style={{ fontSize: 10, color: "#475569", whiteSpace: "nowrap" }}>{resolveAssigneeName(task.defaultAssignedTo)}</span>
                            <button onClick={() => startEditTask(idx)} style={{ background: "transparent", border: "none", color: "#475569", cursor: "pointer", fontSize: 12, padding: "2px 3px" }}>✏️</button>
                            <button onClick={() => removeTask(idx)} style={{ background: "transparent", border: "none", color: "#475569", cursor: "pointer", fontSize: 12, padding: "2px 3px" }}>✕</button>
                          </div>
                        )}
                      </div>
                    ))}
                    {editingTaskIdx === "new" ? (
                      <TaskDraftForm draft={taskDraft} setDraft={setTaskDraft}
                        roleCategories={roleCategories} members={members} user={user}
                        accent={accent} onSave={saveTask} onCancel={() => setEditingTaskIdx(null)} />
                    ) : (
                      <button onClick={startAddTask}
                        style={{ width: "100%", background: "transparent", border: "1px dashed #2D3748", color: "#475569", padding: "7px 10px", borderRadius: 8, fontSize: 11, cursor: "pointer", marginTop: 4 }}>
                        + Add task step
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                  <button onClick={() => setShowBundleForm(false)}
                    style={{ flex: 1, background: "#2D3748", color: "#94A3B8", border: "none", padding: 10, borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Cancel</button>
                  <button onClick={saveBundle}
                    style={{ flex: 2, background: accent, color: "#0F172A", border: "none", padding: 10, borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
                    {editingBundle ? "Save changes" : "Create bundle"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sticky bottom close */}
      <div style={{ position: "sticky", bottom: 0, padding: "12px 16px", background: "rgba(15,23,42,0.95)", borderTop: "1px solid #2D3748", paddingBottom: "calc(12px + env(safe-area-inset-bottom))" }}>
        <button onClick={onClose}
          style={{ width: "100%", background: "#2D3748", border: "none", color: "#F1F5F9", padding: 14, borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
          Close
        </button>
      </div>

      {/* ── Pull modal ──────────────────────────────────────────────────── */}
      {pullTarget && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 400, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div style={{ background: "#1E293B", borderRadius: "16px 16px 0 0", padding: 24, width: "100%", maxWidth: 540, border: "1px solid #2D3748", paddingBottom: "calc(24px + env(safe-area-inset-bottom))" }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#F1F5F9", marginBottom: 4 }}>Add to board</div>
            <div style={{ fontSize: 12, color: "#64748B", marginBottom: 20 }}>{pullTarget.title}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={lS}>Due date</label>
                <input type="date" value={pullDue} onChange={e => setPullDue(e.target.value)} style={iS} />
              </div>
              <div>
                <label style={lS}>Assign to</label>
                <select value={pullAssignee} onChange={e => setPullAssignee(e.target.value)} style={iS}>
                  <option value="">Unassigned</option>
                  {members.map(m => (
                    <option key={m.uid} value={m.uid}>
                      {m.uid === user.uid ? "Me" : m.displayName || m.email}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
              <button onClick={() => setPullTarget(null)}
                style={{ flex: 1, background: "#2D3748", color: "#94A3B8", border: "none", padding: 12, borderRadius: 10, fontSize: 14, cursor: "pointer" }}>Cancel</button>
              <button onClick={confirmPull} disabled={pulling}
                style={{ flex: 2, background: accent, color: "#0F172A", border: "none", padding: 12, borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: "pointer", opacity: pulling ? 0.7 : 1 }}>
                {pulling ? "Adding…" : "Add to board"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Fire event modal ────────────────────────────────────────────── */}
      {fireTarget && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 400, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div className="lib-modal-scroll" style={{ background: "#1E293B", borderRadius: "16px 16px 0 0", padding: 24, width: "100%", maxWidth: 540, border: "1px solid #2D3748", maxHeight: "85vh", overflowY: "auto", paddingBottom: "calc(24px + env(safe-area-inset-bottom))" }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#F1F5F9", marginBottom: 4 }}>Fire event</div>
            <div style={{ fontSize: 12, color: "#64748B", marginBottom: 20 }}>{fireTarget.name}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
              <div>
                <label style={lS}>Context name (optional)</label>
                <input value={fireContextName} onChange={e => setFireContextName(e.target.value)}
                  placeholder="e.g. provider or employee name" style={iS} />
              </div>
              <div>
                <label style={lS}>{fireTarget.anchorLabel}</label>
                <input type="date" value={fireAnchorDate} onChange={e => setFireAnchorDate(e.target.value)} style={iS} />
              </div>
            </div>

            {/* Preview */}
            {fireAnchorDate && (() => {
              const preview = getFirePreview();
              return (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>
                    Preview — {preview.length} tasks
                  </div>
                  <div className="lib-preview-scroll" style={{ maxHeight: 220, overflowY: "auto" }}>
                    {preview.map((t, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", padding: "5px 8px", background: "#0F172A", borderRadius: 6, marginBottom: 3 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#475569", minWidth: 42, textAlign: "right", whiteSpace: "nowrap" }}>{t.due}</span>
                        <span style={{ flex: 1, fontSize: 11, color: "#F1F5F9", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</span>
                        <span style={{ fontSize: 10, color: "#475569", whiteSpace: "nowrap" }}>{t.assignee}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setFireTarget(null)}
                style={{ flex: 1, background: "#2D3748", color: "#94A3B8", border: "none", padding: 12, borderRadius: 10, fontSize: 14, cursor: "pointer" }}>Cancel</button>
              <button onClick={confirmFire} disabled={firing || !fireAnchorDate}
                style={{ flex: 2, background: accent, color: "#0F172A", border: "none", padding: 12, borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: "pointer", opacity: firing || !fireAnchorDate ? 0.5 : 1 }}>
                {firing ? "Generating…" : `Generate ${fireTarget.tasks?.length || 0} tasks`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
